import amapi from '@/api/amapi'
import { getCookie, setCookie, removeCookie } from 'tiny-cookie'

const apiEndpoint = window.sessionStorage.getItem('AMAPI_ENDPOINT') || process.env.VUE_APP_API_ENDPOINT

// Prevent duplicate servicelist calls
let servicesLoadingPromise = null

const initialState = () => ({
  amapiEndpoint: apiEndpoint,
  session_ready: false,
  sid: null,
  session_notice: '',
  profile: {},
  domains: [],
  addons: [],
  domainsPopulated: false,
  services: [],
  servicegroups: [],
  servicesPopulated: false,
  alerts: [],
  ticketBanners: [],
  modal2FAOpened: false,
  accessApprovedList: [], // accesslist endpoint
  accessPendingList: [], // accesslist endpoint
  myAccessList: [], // accountlist endpoint
  restrictedSslTlds: [],
  upgradepromotions: [], // AM-970
})

const state = initialState

const getters = {
  session_ready: state => state.session_ready,
  sid: state => state.sid,
  session_notice: state => state.session_notice,
  isAuthenticated: state => !!state.sid,
  SessionProfile: state => state.profile,
  banned: state => state.profile?.lockout_status,
  banReason: state => state.profile?.lockout_reason,
  // Early-access flag. core only SETS profile.beta (to 1) when the account's
  // client group matches $_CONFIG['am_beta_client_group'] — a non-beta account has
  // no `beta` key at all, so this must be truthy-safe rather than compared to 0.
  // @since GG-993
  betaAccess: state => Number(state.profile?.beta) === 1,
  domains: state => state.domains,
  domainsPopulated: state => state.domainsPopulated,
  services: state => state.services,
  servicesPopulated: state => state.servicesPopulated,
  servicegroups: state => state.servicegroups,
  alerts: state => state.alerts,
  ticketBanners: state => state.ticketBanners,
  ssls: state => state.services.filter(x => x.type === 'ssl'),
  restrictedSslTlds: state => state.restrictedSslTlds,
  sitebuilders: state => state.services.filter(service => service.type === 'builder'),
  amapiEndpoint: state => state.amapiEndpoint,
  accessApprovedList: (state, rootGetters) => {
    return state.accessApprovedList.reduce((carry, item) => {
      const services = rootGetters.services.filter(service => item.services.indexOf(service.id) !== -1)
      carry.push({
        ...item,
        services
      })
      return carry
    }, [])
  },
  accessPendingList: (state, rootGetters) => {
    let received = []
    let sent = []
    if (Array.isArray(state.accessPendingList.received)) {
      received = state.accessPendingList.received.map(item => ({ ...item, type: 'received' }))
    }
    if (Array.isArray(state.accessPendingList.sent)) {
      sent = state.accessPendingList.sent.map(item => ({ ...item, type: 'sent' }))
    }
    const pendingList = [ ...received, ...sent ]
    return pendingList.reduce((carry, item, index) => {
      const service = rootGetters.services.find(service => item.service_id == service.id)
      carry.push({
        ...item,
        service
      })
      return carry
    }, [])
  },
  myAccessList: state => state.myAccessList,
  upgradepromotions: state => state.upgradepromotions,
}

const mutations = {
  SET_SID (state, { sid }) {
    if (sid !== null && sid !== undefined) {
      setCookie('sid', sid, { expires: '7D' })
      state.sid = sid
    } else {
      removeCookie('sid')
      state.sid = ''
    }
  },
  SET_SPECIAL_COOKIE () {
    // AM-356
    // setCookie('GGSDASHBOARD', 'true', {
    //  expires: '1M',
    // })
    // Set GGSDASHBOARD cookie for 30 days
    document.cookie = `GGSDASHBOARD=true; domain=${process.env.VUE_APP_WEBSITE_DOMAIN}; max-age=${30 * 24 * 60 * 60};secure`
  },
  SET_SESSION (state, session) {
    const accountId = session.AccountId
    state.profile = {
      ...session.profile,
      accountId,
    }
    state.session_notice = session.session_notice
  },
  SET_PROFILE (state, profile) {
    state.profile = {
      ...state.profile,
      ...profile,
    }
  },
  SET_SESSION_READY (state, status) {
    state.session_ready = status
  },
  POPULATE_SERVICES (state, params) {
    state.services = params.services
    state.servicegroups = params.servicegroups
    state.servicesPopulated = true
  },
  POPULATE_UPGRADE_BANNERS (state, services) {
    state.upgradepromotions = services.reduce((carry, service) => {
      if (!service.upgradepromotions.length) {
        return carry
      }
      carry.push(...service.upgradepromotions.map(promotion => ({ ...promotion, service_id: service.id })))
      return carry
    }, [])
  },
  POPULATE_ALERTS (state, alerts) {
    state.alerts = alerts
  },
  POPULATE_TICKET_BANNERS (state, banners) {
    state.ticketBanners = banners
  },
  POPULATE_DOMAINS (state, domains) {
    state.domains = domains
    state.domainsPopulated = true
  },
  REMOVE_CACHED_ITEMS () {
    localStorage.removeItem('vuex')
    localStorage.removeItem('domains_list')
    localStorage.removeItem('login')
    localStorage.removeItem('cache_keys')
    sessionStorage.clear()

    removeCookie('sid')
  },
  CLEAR_SESSION_STORE (state) {
    const s = initialState()

    Object.keys(s).forEach(key => {
      state[key] = s[key]
    })
  },
  SET_AMAPI_ENDPOINT (state, endpoint) {
    state.amapiEndpoint = endpoint
  },
  SET_ACCESS_LIST (state, { approvedList, pendingList }) {
    state.accessPendingList = pendingList
    state.accessApprovedList = approvedList || []
  },
  SET_MY_ACCESS_LIST (state, list) {
    state.myAccessList = list
  },
  SET_RESTRICTED_SSL_TLDS (state, tlds = []) {
    state.restrictedSslTlds = tlds
  }
}

const actions = {
  async initializeSession ({ dispatch, commit }) {
    const sid = getCookie('sid')
    if (sid !== null) {
      commit('SET_SID', { sid: sid })
      try {
        await dispatch('getProfile')
      } catch (err) {
        dispatch('reportError', err) // report to a user about the network error
        // AM-301 fix. Handle an error and force relogin.
        commit('SET_SESSION_READY', true)
        return
      }
      commit('SET_SESSION_READY', true)
      dispatch('downtime/getDowntimeList', { root: true })
      dispatch('getSupportToken')
      dispatch('getServices')
      dispatch('migrations/initHiddenMigrations')
      dispatch('migrations/getMigrations')
      dispatch('getTickets')
      dispatch('loadSurvey')
    } else {
      commit('SET_SID', { sid: null })
      dispatch('clearStore')
      commit('SET_SESSION_READY', true)
    }
  },
  async getProfile ({ commit, dispatch }) {
    try {
      const response = await amapi.makecall('getsession')
      dispatch('checkPendingCancellations', response.notices)
      commit('notifications/POPULATE_NOTIFICATIONS', response.notifications, { root: true })
      commit('SET_SESSION', response)
      if (response?.settings?.alphasslrestrictedtlds) {
        commit('SET_RESTRICTED_SSL_TLDS', response.settings.alphasslrestrictedtlds)
      }
      if (response.profile?.onboardmode) {
        localStorage.setItem('redirect', 'quicklaunch')
      }
      const dataLayer = window.dataLayer || []
      dataLayer.push({
        event: 'login',
        userId: response?.AccountId
      })
    } catch (err) {
      commit('SET_SID', { sid: null }) // AM-153
      throw err
    }
  },
  async UpdateProfile ({ commit }, profile) {
    try {
      const response = await amapi.makecall('profile_update', profile)
      if (typeof response.profile === 'object') {
        commit('SET_PROFILE', response.profile)
      }
    } catch (e) {
      throw e
    }
  },
  async attemptLogout ({ dispatch }) {
    try {
      // Clear server session
      await amapi.makecall('logout')
      // Clear last activity time
      localStorage.removeItem('_lastActivity')
      dispatch('clearStore')
      // dispatch('reportLogout', 'Session expired! Please, login to continue.')
    } catch (e) {
      throw e
    }
  },
  async attemptLogin ({ dispatch, commit }, params) {
    try {
      // const reauth_data = {
      //   email: params.username,
      //   password: params.password,
      //   otp: params.otp,
      //   action: params?.action,
      //   method: params?.method,
      //   code: params?.code
      // }
      if (params.sid) {
        const response = await amapi.makecall('reauth', { sid: params.sid })
        if (response.sid) {
          commit('SET_SID', { sid: response.sid, remember: params.remember, reauth: true })
          await dispatch('initializeSession') // TODO: AM-217 servicelist sometimes was requested without sid parameter

          return response
        } else {
          throw response
        }
      } else {
        const response = await amapi.makecall('trylogin', { ...params })
        if (response.sid) {
          commit('SET_SID', { sid: response.sid, remember: params.remember })
          commit('SET_SPECIAL_COOKIE')
          await dispatch('initializeSession')

          window.localStorage.setItem('login', JSON.stringify({ loginAt: Date.now() }))
          dispatch('initRemind2FA')
          return response
        } else {
          return response
        }
      }
    } catch (e) {
      throw e
    }
  },
  async getServices ({ dispatch, commit }, { withDomains } = { withDomains: true }) {
    // Return the in-progress promise if we're already loading
    if (servicesLoadingPromise) {
      return servicesLoadingPromise
    }

    servicesLoadingPromise = (async () => {
      // use withDomains = true to update domains list too
      try {
        const response = await amapi.makecall('servicelist')
        commit('POPULATE_SERVICES', { services: response.services, servicegroups: response.servicegroups })
        commit('POPULATE_UPGRADE_BANNERS', response.services)
        commit('POPULATE_ALERTS', response.alerts)
        commit('POPULATE_DOMAINS', response.domains)
        commit('POPULATE_TICKET_BANNERS', response.ticketbanners)
        // dispatch('checkPendingCancellations', response.notices)
        dispatch('checkServicesForDomainChange')

        if (response.domains.length && withDomains) {
          response.domains.forEach(item => {
            dispatch('queueDomain', item.id)
          })
          await dispatch('queueDomainProcess')
        }

        return response
      } catch (e) {
        throw e
      } finally {
        servicesLoadingPromise = null // clear the promise after completion
      }
    })()

    return servicesLoadingPromise
  },

  async requestResetPassword ({ dispatch }, email) {
    try {
      return amapi.makecall('forgot_password', { email_address: email })
    } catch (e) {
      throw e
    }
  },
  async doRequestPassword ({ dispatch }, payload) {
    try {
      return amapi.makecall('forgot_password_update', { ...payload })
    } catch (e) {
      throw e
    }
  },
  async verifyResetToken ({ dispatch }, token) {
    try {
      return amapi.makecall('forgot_password_verify_token', { token })
    } catch (e) {
      throw e
    }
  },
  async deleteAccountAccess ({ dispatch }, { account, service }) {
    try {
      const response = await amapi.makecall('accountaccessdelete', { account, service })
      dispatch('getAccessList')
      return response
    } catch (e) {
      throw e
    }
  },
  async relinquishSharedAccess ({ dispatch }, { service }) {
    try {
      const response = await amapi.makecall('access_relinquish', { service })
      dispatch('getAccountList')
      dispatch('getServices')
      dispatch('getTickets')
      return response
    } catch (e) {
      throw e
    }
  },
  async getAccessList ({ commit }) {
    try {
      const response = await amapi.makecall('accesslist')
      if (!Array.isArray(response.accounts)) {
        throw new Error('Error loading access list.')
      }
      commit('SET_ACCESS_LIST', { approvedList: response.accounts, pendingList: response.pending })
    } catch (e) {
      throw e
    }
  },
  async getAccountList ({ commit }) {
    try {
      const response = await amapi.makecall('accountlist')
      if (!Array.isArray(response.accounts)) {
        throw new Error('Error loading account list.')
      }
      commit('SET_MY_ACCESS_LIST', response.accounts)
    } catch (e) {
      throw e
    }
  },
  async revokePendingRequest ({ dispatch }, id) {
    try {
      const response = await amapi.makecall('invite_revoke', { id })
      dispatch('getAccessList')
      return response
    } catch (e) {
      throw e
    }
  },
  async acceptPendingRequest ({ dispatch }, id) {
    try {
      const response = await amapi.makecall('invite_accept', { id })
      dispatch('getAccessList')
      dispatch('getAccountList')
      dispatch('getServices')
      return response
    } catch (e) {
      throw e
    }
  },
  async resendPendingRequest ({ dispatch }, id) {
    try {
      const response = await amapi.makecall('invite_resend', { id })
      dispatch('getAccessList')
      return response
    } catch (e) {
      throw e
    }
  },
  async rejectPendingRequest ({ dispatch }, id) {
    try {
      const response = await amapi.makecall('invite_reject', { id })
      dispatch('getAccessList')
      return response
    } catch (e) {
      throw e
    }
  },
  // async sendOtp ({ dispatch }, method) {
  //   try {
  //     return amapi.makecall('send_otp', method)
  //   } catch (e) {
  //     throw e
  //   }
  // },
  clearStore ({ commit, dispatch }) {
    // commit('CLEAR_CRON_STORE')
    commit('CLEAR_DOMAINS_STORE')
    // commit('CLEAR_EMAIL_STORE')
    // The Email dashboard spans every service and caches which ones it has
    // already read, so `emailHub/loadMailboxes` is a no-op on the second visit.
    // Logout and ReAuth both stay in the SPA — nothing reloads the page — so
    // without this the next session in the same tab renders the previous
    // account's mailboxes. AM-1104
    commit('emailHub/CLEAR_MAILBOXES')
    // commit('CLEAR_FTP_STORE')
    commit('CLEAR_HOSTING_STORE')
    commit('hostingDomains/CLEAR_HOSTING_DOMAIN_STORE')
    // commit('CLEAR_HOSTING_SSH_STORE')
    // commit('CLEAR_MYSQL_STORE')
    commit('CLEAR_SESSION_STORE')
    commit('CLEAR_SSL_STORE')
    // commit('CLEAR_SSLTLS_STORE')
    commit('CLEAR_SUPPORT_STORE')
    commit('CLEAR_CANCEL_STORE')

    commit('SET_SID', { sid: null })
    commit('REMOVE_CACHED_ITEMS')
    commit('SET_SESSION_READY', true)
    dispatch('clearRemind2FA')
  },
}

const module = {
  state,
  getters,
  mutations,
  actions
}

export default module
